import { PlanVariantSelectionButtonsStateProps, PlanVariantSelectionButtonsProps } from '../../types/components/pricing/PlanVariantSelectionButtons';
import { SubscriptionDetail, PlanVariantResponse } from '../../types/api/SubscriptionTypes';
import { ApplicationState } from '../../types/state/storeTypes';

import { EssentialsCategoryIconThin } from '../../modules/Icons';
import PlanVariantEssentialPlusCreditsLabel from './PlanVariantEssentialPlusCreditsLabel';

import { connect } from 'react-redux';
import { useEffect, useState } from 'react';

export const activeButtonClass = '!border !border-a-blue !bg-a-blue !text-white';
export const baseClass =
  'flex items-center justify-center max-w-[58px] max-h-[40px] text-[16px] px-[20px] py-[6px] leading-[24px] rounded-[28px] border border-[#7F898D] text-[#7F898D] hover:border-a-blue hover:bg-a-blue hover:bg-opacity-[10%] hover:text-a-blue cursor-pointer transition-colors duration-100';
export const currentPlanClass = 'bg-gray-800 hover:!bg-gray-800 !border !border-gray-800 !text-gray-600';

const PlanVariantSelectionButtons: React.FC<PlanVariantSelectionButtonsProps> = ({
  planVariants,
  selectedPlanVariant,
  interval,
  setSelectedPlanVariant,
  subscription,
}) => {
  const [filteredPlanVariants, setFilteredPlanVariants] = useState<Array<PlanVariantResponse>>();
  const [hoveredButtonIndex, setHoveredButtonIndex] = useState<number>();

  const isSelected = (planVariant: PlanVariantResponse): boolean => selectedPlanVariant?.id === planVariant.id;

  const isHovered = (index: number): boolean => hoveredButtonIndex === index;

  const isOwnedVariant = (planVariant: PlanVariantResponse): boolean => {
    if (!subscription) return false;
    return subscription.plan_variant.group_id === planVariant.group_id;
  }

  const autoSelectPlanVariant = (
    filteredPlanVariants: Array<PlanVariantResponse>,
  ) => {
    const selectedVariant = filteredPlanVariants.find(pv => isOwnedVariant(pv));
    // if user's current plan is legacy and not in the planVariants list,
    // just auto-select the first available plan variant with recurring credits for upgrade
    const plansWithCredits = filteredPlanVariants.filter(pv => !!pv.recurring_credits);
    setSelectedPlanVariant(selectedVariant || plansWithCredits[0]);
  }

  const initializePlanVariants = () => {
    if (!planVariants || !interval) return;

    // filter out rest of the variants to populate the credit amount selection buttons
    const filteredPlanVariants = planVariants.filter(
      variant => variant.interval === interval
    ).sort((a, b) => a.recurring_credits - b.recurring_credits);
    setFilteredPlanVariants(filteredPlanVariants);

    // auto-select logic
    autoSelectPlanVariant(filteredPlanVariants);
  }

  useEffect(initializePlanVariants, [subscription, interval, planVariants]);

  const getButtonClassFor = (planVariant: PlanVariantResponse): string => {
    if (isOwnedVariant(planVariant)) return `${baseClass} ${currentPlanClass}`;
    if (isSelected(planVariant)) return `${baseClass} ${activeButtonClass}`;
    return baseClass;
  }

  const getStrokeColorForEssetnialIcon = (planVariant: PlanVariantResponse, index: number): string => {
    if (isOwnedVariant(planVariant)) return '#7F898D';
    if (isSelected(planVariant)) return '#FFF';
    return isHovered(index) ? '#00A1E0' : '#7F898D';
  }

  return (
    <div className="justify-center lg:pb-6 flex flex-col items-center gap-4 py-4 sm:py-8" cy-test-id="plan-variant-selection-buttons">
      <span className="text-[16px] leading-24 inter whitespace-nowrap" cy-test-id="title">
        Credits per {interval}:
      </span>
      <div className="flex flex-wrap sm:flex-nowrap gap-2" cy-test-id="plus-plan-variants-wrapper">
        {filteredPlanVariants?.map((planVariant, index) => (
          <button
            key={index}
            className={getButtonClassFor(planVariant)}
            onClick={() => setSelectedPlanVariant(planVariant)}
            onMouseEnter={() => setHoveredButtonIndex(index)}
            onMouseLeave={() => setHoveredButtonIndex(undefined)}
            cy-test-id={`plan-variant-${planVariant.id}-button`}
          >
            {
              planVariant.recurring_credits > 0 ?
              planVariant.recurring_credits :
              <EssentialsCategoryIconThin
                fill={getStrokeColorForEssetnialIcon(planVariant, index)}
              />
            }
          </button>
        ))}
      </div>
      <PlanVariantEssentialPlusCreditsLabel planVariant={selectedPlanVariant} />
    </div>
  )
}

const mapStateToProps = (state: ApplicationState): PlanVariantSelectionButtonsStateProps => ({
  subscription: state.auth.subscription as SubscriptionDetail,
});

export default connect(mapStateToProps)(PlanVariantSelectionButtons);
